import { Link } from 'react-router-dom';
import type { ErrorType, IssueReport } from '@/types/model';


const pad2 = (n?: number | null) =>
  typeof n === 'number' && !Number.isNaN(n) ? String(n).padStart(2, '0') : undefined;

const buildMediaLabel = (row: IssueReport) => {
  const t = row.mediaType;
  switch (t) {
    case 'MOVIE':
      return row.mediaTitle ?? row.title ?? `Movie (${row.mediaId ?? row.contentId})`;
    case 'TV_CHANNEL':
      if (row.tvChannelNumber && row.mediaTitle) {
        return `Channel ${row.tvChannelNumber} – ${row.mediaTitle}`;
      }
      if (row.tvChannelNumber) return `Channel ${row.tvChannelNumber}`;
      return row.mediaTitle ?? `TV Channel (${row.mediaId ?? row.contentId})`;
    case 'RADIO_STATION':
      return row.mediaTitle ?? `Radio Station (${row.mediaId ?? row.contentId})`;
    case 'TV_SHOW':
      return row.tvShowTitle ?? row.mediaTitle ?? `TV Show (${row.mediaId ?? row.contentId})`;
    case 'TV_SHOW_EPISODE': {
      const s = pad2(row.seasonNumber);
      const e = pad2(row.episodeNumber);
      const show = row.tvShowTitle ?? 'TV Show';
      const epTitle = row.mediaTitle ? ` – ${row.mediaTitle}` : '';
      if (s && e) return `${show} S${s}E${e}${epTitle}`;
      return row.mediaTitle ?? `${show} Episode (${row.mediaId ?? row.contentId})`;
    }
    case 'DOCUMENTARY':
      return row.mediaTitle ?? `Documentary (${row.mediaId ?? row.contentId})`;
    case 'SPORT':
      return row.mediaTitle ?? `Sport (${row.mediaId ?? row.contentId})`;
    case 'CONCERT':
      return row.mediaTitle ?? `Concert (${row.mediaId ?? row.contentId})`;
    default:
      return row.mediaTitle ?? `${t} (${row.mediaId ?? row.contentId})`;
  }
};

// Build paths adapted to this app's route conventions (/content/*)
const buildMediaPath = (row: IssueReport) => {
  const id = row.mediaId ?? row.contentId; // fallback for older items
  const t = row.mediaType;
  switch (t) {
    case 'MOVIE':
      return `/content/movies/${id}/edit`;
    case 'TV_CHANNEL':
      return `/content/tv-channels/${id}/edit`;
    case 'RADIO_STATION':
      return `/content/radio-stations/${id}/edit`;
    case 'TV_SHOW':
      return `/content/tv-shows/${id}/edit`;
    case 'TV_SHOW_EPISODE': {
      // App has no nested season/episode detail route; fall back to show details when possible
      const showId = row.tvShowId ?? id;
      return `/content/tv-shows/${showId}/episodes`;
    }
    case 'DOCUMENTARY':
      return `/content/documentaries/${id}/edit`;
    case 'SPORT':
      return `/content/sports/${id}/edit`;
    case 'CONCERT':
      return `/content/concerts/${id}/edit`;
    default:
      return `/content`; // generic fallback
  }
};

export const TitleCell = ({ row }: { row: IssueReport }) => {
  const label = buildMediaLabel(row);
  const to = buildMediaPath(row);
  return (
    <Link to={to} title={label} className="text-indigo-600 hover:text-indigo-800 truncate max-w-48">
      {label}
    </Link>
  );
};

export const ErrorTypeCell = ({ type }: { type: ErrorType }) => {
  switch (type) {
    case 'IMAGE_URL_NOT_FOUND':
      return 'Image URL Not Found';
    case 'PLAYBACK_ERROR':
      return 'Error de reproducción';
    case 'TRAILER_PLAYBACK_ERROR':
      return 'Error de trailer';
    case 'BACKGROUND_IMAGE_ERROR':
      return 'Image de fondo con error';
    case 'COVER_IMAGE_ERROR':
      return 'Imagen de portada con error';
    case 'SUBTITLE_ERROR':
      return 'Problema con los subtítulos';
    case 'VIDEO_WONT_PLAY':
      return 'El video no inicia';
    case 'VIDEO_BUFFERING_ISSUE':
      return 'Video se detiene constantemente';
    case 'VIDEO_LOW_QUALITY':
      return 'Video de baja calidad'
    case "APP_CRASHED":
      return 'La app falla'
    case "AUDIO_ERROR":
      return 'Audio desincronizado o con problemas'
    default:
      return 'Error desconocido';
  }
}

